/**
 * Global Styles for Sankofa Stream
 * African-inspired aesthetic with Kente patterns and earth tones
 */

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800;900&family=Crimson+Text:wght@400;600;700&family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap');

  :root {
    --bg-primary: #0d0806;
    --bg-secondary: #1a120f;
    --bg-tertiary: #2a1d17;
    --accent-gold: #d4af37;
    --accent-amber: #e67e22;
    --accent-terracotta: #c44536;
    --accent-earth: #8b6f47;
    --text-primary: #f5f0eb;
    --text-secondary: #c9b9a8;
    --text-tertiary: #8a7a6b;
    --success: #27ae60;
    --kente-red: #d32f2f;
    --kente-gold: #ffd700;
    --kente-green: #2e7d32;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
  }

  body {
    font-family: 'Cormorant Garamond', serif;
    background: var(--bg-primary);
    color: var(--text-primary);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    background-image: 
      radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(196, 69, 54, 0.05) 0%, transparent 50%);
  }

  .app-container {
    min-height: 100vh;
    padding-bottom: 90px;
    position: relative;
  }

  /* African Pattern Overlay */
  .app-container::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: 
      repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(212, 175, 55, 0.02) 2px, rgba(212, 175, 55, 0.02) 4px),
      repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(212, 175, 55, 0.02) 2px, rgba(212, 175, 55, 0.02) 4px);
    pointer-events: none;
    z-index: 1;
  }

  /* Kente Pattern Accent */
  .kente-border {
    position: relative;
  }

  .kente-border::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, 
      var(--kente-red) 0%, 
      var(--kente-red) 20%, 
      var(--kente-gold) 20%, 
      var(--kente-gold) 40%, 
      var(--kente-green) 40%, 
      var(--kente-green) 60%,
      var(--kente-gold) 60%, 
      var(--kente-gold) 80%,
      var(--kente-red) 80%,
      var(--kente-red) 100%
    );
  }

  /* Navigation Bar */
  .nav-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(180deg, rgba(26, 18, 15, 0.95) 0%, rgba(13, 8, 6, 0.98) 100%);
    backdrop-filter: blur(20px);
    border-top: 2px solid var(--accent-gold);
    padding: 16px 20px 24px;
    z-index: 1000;
    display: flex;
    justify-content: space-around;
    align-items: center;
  }

  .nav-bar::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      var(--kente-red), var(--kente-gold), var(--kente-green), var(--kente-gold), var(--kente-red)
    );
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    background: none;
    color: var(--text-tertiary);
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 12px;
    position: relative;
    font-family: 'Crimson Text', serif;
  }

  .nav-item.active {
    color: var(--accent-gold);
  }

  .nav-item.active::before {
    content: '';
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--accent-gold), transparent);
    animation: slideDown 0.4s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  .nav-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  /* Header */
  .header {
    padding: 24px 20px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(180deg, rgba(13, 8, 6, 0.9) 0%, rgba(13, 8, 6, 0) 100%);
    position: relative;
    z-index: 10;
  }

  .logo-container {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .logo {
    font-size: 32px;
    font-weight: 900;
    letter-spacing: 2px;
    background: linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-amber) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-family: 'Playfair Display', serif;
    text-transform: uppercase;
  }

  .logo-subtitle {
    font-size: 9px;
    letter-spacing: 3px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    font-family: 'Crimson Text', serif;
    font-weight: 600;
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }

  .icon-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid rgba(212, 175, 55, 0.2);
    background: rgba(42, 29, 23, 0.6);
    color: var(--accent-gold);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
  }

  .icon-btn:active {
    transform: scale(0.95);
    background: rgba(212, 175, 55, 0.1);
    border-color: var(--accent-gold);
  }

  /* Featured Content */
  .featured-section {
    padding: 0 20px 32px;
    position: relative;
  }

  .featured-card {
    position: relative;
    height: 550px;
    border-radius: 20px;
    overflow: hidden;
    background: var(--bg-secondary);
    border: 2px solid rgba(212, 175, 55, 0.15);
  }

  .featured-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: contrast(1.1) brightness(0.9);
  }

  .featured-gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, 
      rgba(13, 8, 6, 0.2) 0%, 
      rgba(13, 8, 6, 0.6) 50%, 
      rgba(13, 8, 6, 0.95) 100%
    );
  }

  .featured-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 28px;
  }

  .heritage-badge {
    display: inline-block;
    padding: 6px 14px;
    background: rgba(212, 175, 55, 0.15);
    border: 1px solid var(--accent-gold);
    border-radius: 20px;
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--accent-gold);
    font-weight: 700;
    margin-bottom: 12px;
    font-family: 'Crimson Text', serif;
  }

  .featured-title {
    font-size: 38px;
    font-weight: 900;
    margin-bottom: 8px;
    letter-spacing: 1px;
    font-family: 'Playfair Display', serif;
    line-height: 1.1;
  }

  .featured-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    font-size: 13px;
    color: var(--text-secondary);
    flex-wrap: wrap;
    font-family: 'Crimson Text', serif;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .rating {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--accent-gold);
    font-weight: 700;
  }

  .restored-badge {
    background: linear-gradient(135deg, var(--success) 0%, #27ae60 100%);
    color: white;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
  }

  .featured-description {
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 20px;
    font-size: 15px;
    font-family: 'Cormorant Garamond', serif;
  }

  .featured-actions {
    display: flex;
    gap: 12px;
  }

  .btn-primary {
    flex: 1;
    padding: 18px;
    border: none;
    border-radius: 16px;
    background: linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-amber) 100%);
    color: var(--bg-primary);
    font-weight: 700;
    font-size: 17px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Crimson Text', serif;
    letter-spacing: 1px;
    text-transform: uppercase;
    box-shadow: 0 8px 24px rgba(212, 175, 55, 0.3);
  }

  .btn-primary:active {
    transform: translateY(2px);
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  }

  .btn-secondary {
    width: 60px;
    height: 60px;
    border: 2px solid var(--accent-gold);
    border-radius: 16px;
    background: rgba(42, 29, 23, 0.8);
    backdrop-filter: blur(10px);
    color: var(--accent-gold);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .btn-secondary:active {
    transform: scale(0.95);
    background: rgba(212, 175, 55, 0.15);
  }

  /* Categories */
  .categories {
    padding: 0 20px 24px;
    overflow-x: auto;
    display: flex;
    gap: 12px;
    scrollbar-width: none;
  }

  .categories::-webkit-scrollbar {
    display: none;
  }

  .category-chip {
    padding: 12px 24px;
    border-radius: 24px;
    border: 1px solid rgba(212, 175, 55, 0.2);
    background: rgba(42, 29, 23, 0.4);
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Crimson Text', serif;
    letter-spacing: 0.5px;
  }

  .category-chip.active {
    background: linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-amber) 100%);
    color: var(--bg-primary);
    border-color: var(--accent-gold);
  }

  /* Content Grid */
  .content-section {
    padding: 0 20px 40px;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 0.5px;
    font-family: 'Playfair Display', serif;
  }

  .see-all {
    color: var(--accent-gold);
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    border: none;
    background: none;
    font-family: 'Crimson Text', serif;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .content-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .content-card {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    background: var(--bg-secondary);
    border: 1px solid rgba(212, 175, 55, 0.1);
  }

  .content-card:active {
    transform: translateY(4px);
  }

  .content-card-image {
    width: 100%;
    aspect-ratio: 2/3;
    object-fit: cover;
    filter: contrast(1.05) brightness(0.95);
  }

  .content-card-info {
    padding: 14px;
    background: linear-gradient(180deg, transparent 0%, rgba(13, 8, 6, 0.9) 50%);
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }

  .content-card-title {
    font-size: 17px;
    font-weight: 700;
    margin-bottom: 4px;
    font-family: 'Playfair Display', serif;
  }

  .content-card-meta {
    font-size: 12px;
    color: var(--text-tertiary);
    font-family: 'Crimson Text', serif;
  }

  .quality-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(39, 174, 96, 0.95);
    color: white;
    padding: 4px 10px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    backdrop-filter: blur(10px);
  }

  /* Search Page */
  .search-container {
    padding: 20px;
  }

  .search-bar {
    position: relative;
    margin-bottom: 28px;
  }

  .search-input {
    width: 100%;
    padding: 18px 52px 18px 52px;
    border-radius: 16px;
    border: 2px solid rgba(212, 175, 55, 0.2);
    background: rgba(42, 29, 23, 0.4);
    color: var(--text-primary);
    font-size: 16px;
    font-family: 'Cormorant Garamond', serif;
    outline: none;
    transition: all 0.3s ease;
  }

  .search-input:focus {
    border-color: var(--accent-gold);
    background: rgba(42, 29, 23, 0.6);
  }

  .search-input::placeholder {
    color: var(--text-tertiary);
  }

  .search-icon {
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--accent-gold);
  }

  .clear-search {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: rgba(212, 175, 55, 0.15);
    color: var(--accent-gold);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .recent-searches {
    margin-bottom: 32px;
  }

  .recent-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0;
    border-bottom: 1px solid rgba(212, 175, 55, 0.1);
    cursor: pointer;
  }

  .recent-item-text {
    display: flex;
    align-items: center;
    gap: 14px;
    color: var(--text-secondary);
    font-family: 'Crimson Text', serif;
  }

  /* Player Page */
  .player-container {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: #000;
  }

  .video-wrapper {
    flex: 1;
    position: relative;
    background: #000;
  }

  .video-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: linear-gradient(135deg, #1a120f, #0d0806);
  }

  .player-controls {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.9) 100%);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 24px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .player-controls.visible {
    opacity: 1;
  }

  .player-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .player-footer {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .progress-bar {
    width: 100%;
    height: 5px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    position: relative;
    cursor: pointer;
  }

  .progress-fill {
    height: 100%;
    background: var(--accent-gold);
    border-radius: 3px;
    transition: width 0.1s linear;
  }

  .playback-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 28px;
  }

  .play-pause-btn {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    border: none;
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-amber));
    color: var(--bg-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 24px rgba(212, 175, 55, 0.4);
  }

  .play-pause-btn:active {
    transform: scale(0.95);
  }

  /* Profile Page */
  .profile-container {
    padding: 20px;
  }

  .profile-header {
    text-align: center;
    padding: 40px 0;
    border-bottom: 1px solid rgba(212, 175, 55, 0.2);
    margin-bottom: 28px;
  }

  .profile-avatar {
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-amber));
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 44px;
    font-weight: 700;
    border: 3px solid rgba(212, 175, 55, 0.3);
    font-family: 'Playfair Display', serif;
  }

  .profile-name {
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 6px;
    font-family: 'Playfair Display', serif;
  }

  .profile-email {
    color: var(--text-tertiary);
    font-size: 15px;
    font-family: 'Crimson Text', serif;
  }

  .profile-menu {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px;
    border-radius: 14px;
    background: rgba(42, 29, 23, 0.3);
    border: 1px solid rgba(212, 175, 55, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    color: var(--text-primary);
    font-size: 16px;
    font-family: 'Crimson Text', serif;
    text-align: left;
  }

  .menu-item:active {
    background: rgba(212, 175, 55, 0.1);
    border-color: var(--accent-gold);
  }

  .menu-item-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  /* Detail Page */
  .detail-container {
    min-height: 100vh;
    padding-bottom: 90px;
  }

  .detail-hero {
    position: relative;
    height: 450px;
  }

  .detail-hero-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: contrast(1.1) brightness(0.85);
  }

  .detail-gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(13,8,6,0) 0%, rgba(13,8,6,0.8) 70%, rgba(13,8,6,1) 100%);
  }

  .detail-back-btn {
    position: absolute;
    top: 24px;
    left: 20px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid rgba(212, 175, 55, 0.3);
    background: rgba(13, 8, 6, 0.8);
    backdrop-filter: blur(10px);
    color: var(--accent-gold);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
  }

  .detail-content {
    padding: 0 20px;
    margin-top: -90px;
    position: relative;
    z-index: 1;
  }

  .detail-title {
    font-size: 36px;
    font-weight: 900;
    margin-bottom: 14px;
    letter-spacing: 0.5px;
    font-family: 'Playfair Display', serif;
  }

  .detail-meta {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    font-size: 14px;
    color: var(--text-secondary);
    flex-wrap: wrap;
    font-family: 'Crimson Text', serif;
  }

  .detail-actions {
    display: flex;
    gap: 12px;
    margin-bottom: 28px;
  }

  .detail-description {
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 36px;
    font-size: 16px;
    font-family: 'Cormorant Garamond', serif;
  }

  .detail-section {
    margin-bottom: 36px;
  }

  .detail-section-title {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 18px;
    font-family: 'Playfair Display', serif;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }

  .info-item {
    background: rgba(42, 29, 23, 0.4);
    border: 1px solid rgba(212, 175, 55, 0.1);
    padding: 16px;
    border-radius: 12px;
  }

  .info-label {
    font-size: 12px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
    font-family: 'Crimson Text', serif;
  }

  .info-value {
    font-size: 16px;
    color: var(--text-primary);
    font-weight: 600;
    font-family: 'Cormorant Garamond', serif;
  }

  /* Downloads Page */
  .downloads-container {
    padding: 20px;
  }

  .downloads-empty {
    text-align: center;
    padding: 100px 20px;
  }

  .empty-icon {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(42, 29, 23, 0.4);
    border: 2px solid rgba(212, 175, 55, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: var(--accent-gold);
  }

  .empty-title {
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 10px;
    font-family: 'Playfair Display', serif;
  }

  .empty-text {
    color: var(--text-tertiary);
    line-height: 1.6;
    font-family: 'Cormorant Garamond', serif;
  }

  /* Animations */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .fade-in-up {
    animation: fadeInUp 0.6s ease;
  }

  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .loading-shimmer {
    background: linear-gradient(90deg, 
      rgba(212, 175, 55, 0.05) 0%, 
      rgba(212, 175, 55, 0.1) 50%, 
      rgba(212, 175, 55, 0.05) 100%
    );
    background-size: 1000px 100%;
    animation: shimmer 2s infinite;
  }

  /* Responsive adjustments */
  @media (max-width: 380px) {
    .featured-title {
      font-size: 32px;
    }
    .detail-title {
      font-size: 30px;
    }
  }
    .auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
}

.auth-content {
  width: 100%;
  max-width: 440px;
  background: rgba(42, 29, 23, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 24px;
  padding: 48px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

/* Auth Header */
.auth-header {
  text-align: center;
  margin-bottom: 36px;
}

.auth-logo {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, var(--accent-gold), var(--accent-amber));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  border: 3px solid rgba(212, 175, 55, 0.3);
}

.auth-title {
  font-size: 32px;
  font-weight: 900;
  margin-bottom: 8px;
  font-family: 'Playfair Display', serif;
  background: linear-gradient(135deg, var(--accent-gold), var(--accent-amber));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.auth-subtitle {
  color: var(--text-secondary);
  font-size: 16px;
  font-family: 'Cormorant Garamond', serif;
}

/* Auth Form */
.auth-form {
  margin-bottom: 28px;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Crimson Text', serif;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  color: var(--accent-gold);
  pointer-events: none;
}

.form-input {
  width: 100%;
  padding: 16px 16px 16px 48px;
  background: rgba(13, 8, 6, 0.6);
  border: 2px solid rgba(212, 175, 55, 0.2);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 16px;
  font-family: 'Cormorant Garamond', serif;
  outline: none;
  transition: all 0.3s ease;
}

.form-input:focus {
  border-color: var(--accent-gold);
  background: rgba(13, 8, 6, 0.8);
  box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.1);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

.password-toggle {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
}

.password-toggle:hover {
  color: var(--accent-gold);
}

/* Form Footer */
.form-footer {
  text-align: right;
  margin-bottom: 24px;
}

.forgot-link {
  color: var(--accent-gold);
  font-size: 14px;
  font-family: 'Crimson Text', serif;
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.forgot-link:hover {
  opacity: 0.8;
}

.form-terms {
  margin-bottom: 24px;
  text-align: center;
}

.form-terms p {
  font-size: 13px;
  color: var(--text-tertiary);
  line-height: 1.6;
  font-family: 'Cormorant Garamond', serif;
}

.terms-link {
  color: var(--accent-gold);
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.terms-link:hover {
  opacity: 0.8;
}

/* Auth Button */
.auth-btn {
  width: 100%;
  margin-bottom: 0;
}

/* Error Message */
.auth-error {
  background: rgba(196, 69, 54, 0.15);
  border: 1px solid var(--accent-terracotta);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 24px;
  color: #ff6b6b;
  font-size: 14px;
  font-family: 'Crimson Text', serif;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Loading Spinner */
.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* Divider */
.auth-divider {
  position: relative;
  text-align: center;
  margin: 32px 0;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: rgba(212, 175, 55, 0.2);
}

.auth-divider::before {
  left: 0;
}

.auth-divider::after {
  right: 0;
}

.auth-divider span {
  background: rgba(42, 29, 23, 0.6);
  padding: 0 16px;
  color: var(--text-tertiary);
  font-size: 13px;
  font-family: 'Crimson Text', serif;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* Google Button */
.btn-google {
  width: 100%;
  padding: 16px;
  background: white;
  border: 2px solid rgba(212, 175, 55, 0.2);
  border-radius: 12px;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Crimson Text', serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 24px;
}

.btn-google:hover {
  background: #f8f8f8;
  border-color: var(--accent-gold);
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.btn-google:active {
  transform: translateY(0);
}

.btn-google:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Auth Switch */
.auth-switch {
  text-align: center;
}

.auth-switch p {
  color: var(--text-secondary);
  font-size: 15px;
  font-family: 'Cormorant Garamond', serif;
}

.auth-link {
  color: var(--accent-gold);
  font-weight: 700;
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.auth-link:hover {
  opacity: 0.8;
}

/* Sign Out Button */
.sign-out-btn {
  margin-top: 28px;
  color: var(--accent-gold);
  border-color: rgba(212, 175, 55, 0.3);
}

.sign-out-btn:hover {
  background: rgba(212, 175, 55, 0.15);
  border-color: var(--accent-gold);
}

/* Responsive */
@media (max-width: 480px) {
  .auth-content {
    padding: 36px 24px;
  }

  .auth-title {
    font-size: 28px;
  }

  .auth-subtitle {
    font-size: 14px;
  }
}
`;

export default globalStyles;